import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { bracketApi } from '@/services/api';
import { getGameById } from '@/data/tournament';
import toast from 'react-hot-toast';
import type { BracketPick, Bracket, BracketRegion } from '@bracketblitz/shared-types';

interface BracketState {
  // Current bracket data
  bracket: Bracket | null;
  picks: Record<string, BracketPick>; // gameId -> BracketPick (changed from Map to plain object for Immer compatibility)
  lastRequestedBracketId: string | null;
  lastStableBracket: Bracket | null;
  
  // UI state
  isBootstrapping: boolean; // initial load of user's bracket (full-screen loader)
  isLoadingBracket: boolean; // load specific bracket
  isCreatingBracket: boolean; // creating bracket
  isUpdatingPick: boolean; // per-pick updates (avoid full-page loaders)
  isSubmitting: boolean;
  isSubmitted: boolean;
  error: string | null;
  
  // Progress tracking
  totalPicks: number;
  completedPicks: number;
  
  // Actions
  updatePick: (gameId: string, teamId: string, confidence?: number) => Promise<void>;
  submitBracket: (tiebreakerPoints?: number) => Promise<void>;
  loadBracket: (bracketId: string) => Promise<void>;
  loadBracketFallbackToOwned: (targetBracketId?: string) => Promise<Bracket | null>;
  loadUserBracket: () => Promise<void>;
  createBracket: (name: string, tournamentId: string, description?: string) => Promise<void>;
  resetBracket: () => void;
  clearError: () => void;
  
  // Computed getters
  getPicksProgress: () => { completed: number; total: number; percentage: number };
  getRegionProgress: (region: BracketRegion) => { completed: number; total: number; percentage: number };
  getPickForGame: (gameId: string) => BracketPick | null;
  getRegionPicks: (region: BracketRegion) => BracketPick[];
  canSubmitBracket: () => boolean;
}

const TOTAL_GAMES = 63; // 63 bracket games (First Four auto-resolved for demo)

const parseGameIndex = (id: string) => {
  const m = id.match(/game-(\d+)-(\d+)/);
  if (!m) return { roundGroup: Number.MAX_SAFE_INTEGER, index: Number.MAX_SAFE_INTEGER };
  return {
    roundGroup: Number(m[1]),
    index: Number(m[2]),
  };
};

const getFeederSlotByNumericOrder = (targetGameId: string, feederGameId: string): 'team1' | 'team2' => {
  const target = parseGameIndex(targetGameId);
  const feeder = parseGameIndex(feederGameId);

  if (target.roundGroup !== Number.MAX_SAFE_INTEGER && feeder.roundGroup !== Number.MAX_SAFE_INTEGER) {
    if (feeder.roundGroup === target.roundGroup - 1 && feeder.index >= 1) {
      return feeder.index % 2 === 1 ? 'team1' : 'team2';
    }
  }

  return feeder.index % 2 === 1 ? 'team1' : 'team2';
};

const resolveCanonicalSourceSlot = (targetGameId: string, feederGameId: string): 'team1' | 'team2' => {
  const target = parseGameIndex(targetGameId);
  const feeder = parseGameIndex(feederGameId);

  if (target.roundGroup === Number.MAX_SAFE_INTEGER || feeder.roundGroup === Number.MAX_SAFE_INTEGER) {
    return getFeederSlotByNumericOrder(targetGameId, feederGameId);
  }

  // Explicit bracket rule: source in odd/even slot for its pair into next round.
  if (feeder.roundGroup + 1 !== target.roundGroup) {
    return getFeederSlotByNumericOrder(targetGameId, feederGameId);
  }

  const expectedTop = `${feeder.roundGroup}-${(target.index * 2) - 1}`;
  const expectedBottom = `${feeder.roundGroup}-${target.index * 2}`;
  const feederSuffix = `${feeder.roundGroup}-${feeder.index}`;

  if (feederSuffix === expectedTop) return 'team1';
  if (feederSuffix === expectedBottom) return 'team2';

  return getFeederSlotByNumericOrder(targetGameId, feederGameId);
};

let loadUserBracketSeq = 0;
const updatePickSeqByGame: Record<string, number> = {};

export const useBracketStore = create<BracketState>()(
  devtools(
    subscribeWithSelector(
      immer((set, get) => {
        const loadPicksFromBracket = (state: any, rawBracket: any) => {
          state.bracket = rawBracket;
          state.lastStableBracket = rawBracket || null;
          state.isSubmitted = rawBracket.isLocked;
          state.picks = {};
          if (rawBracket.picks) {
            rawBracket.picks.forEach((pick: BracketPick) => {
              const game = getGameById(pick.gameId);
              state.picks[pick.gameId] = {
                ...pick,
                region: pick.region || game?.region || 'east',
              };
            });
          }
          state.completedPicks = Object.keys(state.picks).length;
        };

          const clearErrorToNoBracket = (state: any, reason: string, reasonBracketId: string | null) => {
          state.error = reason;
          state.isLoadingBracket = false;
          state.isUpdatingPick = false;
          if (!state.lastStableBracket) {
            state.bracket = null;
            state.lastRequestedBracketId = state.lastRequestedBracketId || reasonBracketId;
          } else {
            state.bracket = state.lastStableBracket;
            state.picks = state.picks || {};
            state.completedPicks = Object.keys(state.picks).length;
          }
        };

        const normalizeBracket = (input: any) => ({
            ...input,
            id: input?.id || input?.bracketId || input?.bracket_id || input?._id,
            isLocked: Boolean(input?.isLocked ?? input?.is_locked),
            isActive: Boolean(input?.isActive ?? input?.is_active),
            userId: input?.userId || input?.user_id,
            createdAt: input?.createdAt || input?.created_at,
            updatedAt: input?.updatedAt || input?.updated_at,
            picks: Array.isArray(input?.picks) ? input.picks : [],
          });

        const getErrorStatus = (error: unknown): number | undefined => {
          if (!error || typeof error !== 'object') return undefined;
          const candidate = error as {
            status?: unknown;
            statusCode?: unknown;
            response?: { status?: unknown; statusCode?: unknown };
          };
          const status = candidate.status ?? candidate.statusCode;
          const responseStatus = candidate.response?.status ?? candidate.response?.statusCode;
          if (typeof status === 'number') return status;
          if (typeof status === 'string' && status.trim()) {
            const parsed = Number(status);
            return Number.isNaN(parsed) ? undefined : parsed;
          }
          if (typeof responseStatus === 'number') return responseStatus;
          if (typeof responseStatus === 'string' && responseStatus.trim()) {
            const parsed = Number(responseStatus);
            return Number.isNaN(parsed) ? undefined : parsed;
          }
          return undefined;
        };



        const pickWriteQueues: Record<string, Promise<void>> = {};
        let pickWriteSeq = 0;
        const tracePickFlow = (event: string, payload: Record<string, unknown>) => {
          const entry = {
            ts: new Date().toISOString(),
            event,
            ...payload,
          };

          if (typeof console !== 'undefined' && console.debug) {
            console.debug('[BracketPick]', entry);
          }

          if (typeof window !== 'undefined') {
            try {
              const key = 'bracket_pick_trace';
              const existing = JSON.parse(window.localStorage.getItem(key) || '[]');
              if (Array.isArray(existing)) {
                existing.push(entry);
                window.localStorage.setItem(key, JSON.stringify(existing.slice(-40)));
              }
            } catch {
              // noop if storage unavailable
            }
          }
        };

        const traceBracketState = (reason: string, nextBracketId: string | null, extra: Record<string, unknown> = {}) => {
          if (typeof window === 'undefined') return;
          try {
            const key = 'bracket_state_trace';
            const current = JSON.parse(window.localStorage.getItem(key) || '[]');
            const entry = {
              ts: new Date().toISOString(),
              reason,
              nextBracketId,
              ...extra,
            };
            const next = Array.isArray(current) ? current : [];
            next.push(entry);
            window.localStorage.setItem(key, JSON.stringify(next.slice(-80)));
            if (typeof console !== 'undefined' && console.debug) {
              console.debug('[BracketState]', entry);
            }
          } catch {
            // no-op
          }
        };

        const runPickWrite = async (queueKey: string, fn: () => Promise<void>) => {
          const previous = pickWriteQueues[queueKey] ?? Promise.resolve();
          let next = previous
            .catch(() => {})
            .then(fn)
            .finally(() => {
              if (pickWriteQueues[queueKey] === next) {
                delete pickWriteQueues[queueKey];
              }
            });

          pickWriteQueues[queueKey] = next;
          return next;
        };

        return {
// Initial state
        bracket: null,
        picks: {},
        lastRequestedBracketId: null,
        lastStableBracket: null,
        isBootstrapping: false,
        isLoadingBracket: false,
        isCreatingBracket: false,
        isUpdatingPick: false,
        isSubmitting: false,
        isSubmitted: false,
        error: null,
        totalPicks: TOTAL_GAMES,
        completedPicks: 0,

        // Actions
        updatePick: async (gameId: string, teamId: string, confidence = 5) => {
          const attemptId = `pp-${Date.now()}-${++pickWriteSeq}`;
          const routeScopeFromPath = () => {
            if (typeof window === 'undefined') return null;
            const m = window.location.pathname.match(/\/bracket\/([^/]+)/);
            return m ? m[1] : null;
          };
          const currentRouteScope = routeScopeFromPath();
          const queueScope = `route:${currentRouteScope || get().lastRequestedBracketId || get().bracket?.id || 'active'}`;
          const routeScope = currentRouteScope || get().lastRequestedBracketId;
          const teamIdTrimmed = teamId.trim();

          const operation = async (allowRetry = true) => {
            let { bracket } = get();
            const mySeq = (updatePickSeqByGame[gameId] || 0) + 1;
            updatePickSeqByGame[gameId] = mySeq;
            let recoveredOnce = false;

            const isHydrating = get().isBootstrapping || get().isLoadingBracket || get().isCreatingBracket;
            if (isHydrating) {
              tracePickFlow('write.blocked.hydrating', {
                attemptId,
                gameId,
                routeScope,
                bracketId: bracket?.id,
              });
              if (!get().error) {
                set((state) => {
                  state.error = 'Bracket is still loading. Try again.';
                });
              }
              toast('Bracket is still syncing. Retrying…', { icon: '🔄' });
              return;
            }

            const tryRecoverBracket = async () => {
              const targetId = currentRouteScope || get().lastRequestedBracketId;
              if (targetId) {
                tracePickFlow('write.recovery.tryLoadBracket', {
                  attemptId,
                  targetId,
                });
                await get().loadBracket(targetId);
              } else {
                tracePickFlow('write.recovery.tryLoadActive', {
                  attemptId,
                });
                await get().loadUserBracket();
              }
              bracket = get().bracket;
            };

            const ensureWriteReady = async () => {
              if (bracket && routeScope && bracket.id !== routeScope) {
                tracePickFlow('write.recovery.routeMismatch', {
                  attemptId,
                  expected: routeScope,
                  observed: bracket.id,
                });
                await tryRecoverBracket();
                return;
              }

              if (!bracket) {
                tracePickFlow('write.recovery.missing', { attemptId, gameId, routeScope });
                await tryRecoverBracket();
              }
            };

            await ensureWriteReady();
            if (!bracket) {
              tracePickFlow('write.fail.unready', {
                attemptId,
                gameId,
                routeScope,
                routePath: currentRouteScope,
              });

              const recoveryFallback = get().lastStableBracket;
              if (recoveryFallback && (!routeScope || recoveryFallback.id === routeScope)) {
                set((state) => {
                  state.bracket = recoveryFallback;
                  state.lastRequestedBracketId = recoveryFallback.id;
                  state.lastStableBracket = recoveryFallback;
                  state.isLoadingBracket = false;
                });
                const fallbackState = get().bracket;
                if (fallbackState) {
                  const recoveredBracket = fallbackState;
                  tracePickFlow('write.recovery.stableFallback', {
                    attemptId,
                    recoveredId: recoveredBracket.id,
                    routeScope,
                  });
                  await tryRecoverBracket();
                  await operation(false);
                  return;
                }
              }

              set((state) => {
                state.error = 'This bracket cannot be updated right now. Open your active bracket to continue.';
              });
              return;
            }

            const activeBracketId = bracket.id;
            tracePickFlow('write.start', {
              attemptId,
              gameId,
              bracketId: activeBracketId,
              routeScope,
              teamId: teamIdTrimmed,
            });

            if (bracket.isLocked) {
              set((state) => {
                state.error = 'Bracket is locked and cannot be modified';
              });
              return;
            }

            set((state) => {
              state.isUpdatingPick = true;
              state.error = null;
            });

            try {
              const updatedPick = await bracketApi.updatePick(activeBracketId, {
                gameId,
                teamId: teamIdTrimmed,
                confidence,
              });

              if (updatePickSeqByGame[gameId] !== mySeq) {
                tracePickFlow('write.stale', { attemptId, gameId, mySeq, latest: updatePickSeqByGame[gameId] });
                set((state) => {
                  state.isUpdatingPick = false;
                });
                return;
              }

              // Winner propagation (client-side) so later rounds become pickable
              const propagateWinnerAndClearInvalid = (sourceGameId: string, winnerTeamId: string) => {
                const sourceGame = getGameById(sourceGameId);
                if (!sourceGame?.nextGameId) return;

                const nextGame = getGameById(sourceGame.nextGameId);
                if (!nextGame) return;

                const sourceSlot = resolveCanonicalSourceSlot(sourceGame.nextGameId, sourceGameId);
                const slot = sourceSlot === 'team1' ? 'team1Id' : 'team2Id';
                const seedSlot = sourceSlot === 'team1' ? 'team1Seed' : 'team2Seed';

                const projectedNext = {
                  ...nextGame,
                  [slot]: winnerTeamId,
                  [seedSlot]: winnerTeamId === sourceGame.team1Id ? sourceGame.team1Seed : sourceGame.team2Seed,
                } as any;

                const downstreamPick = get().picks[nextGame.id];
                const team1 = projectedNext.team1Id;
                const team2 = projectedNext.team2Id;
                const teamsMissing = !team1 || !team2 || team1 === 'tbd' || team2 === 'tbd';
                const nextTeams = new Set([team1, team2].filter(Boolean));

                if (downstreamPick && teamsMissing) {
                  set((state) => {
                    delete state.picks[nextGame.id];
                    state.completedPicks = Object.keys(state.picks).length;
                  });
                  return;
                }

                if (downstreamPick && !nextTeams.has(downstreamPick.teamId)) {
                  set((state) => {
                    delete state.picks[nextGame.id];
                    state.completedPicks = Object.keys(state.picks).length;
                  });
                }

                const currentDownstreamPick = get().picks[nextGame.id];
                if (currentDownstreamPick) {
                  propagateWinnerAndClearInvalid(nextGame.id, currentDownstreamPick.teamId);
                }
              };

              set((state) => {
                const game = getGameById(gameId);
                const pickRegion = (updatedPick as any).region || game?.region || 'east';

                const pick: BracketPick = {
                  id: updatedPick.id || `pick-${gameId}`,
                  bracketId: activeBracketId,
                  gameId,
                  teamId: teamIdTrimmed,
                  round: (updatedPick as any).round || 'first_round',
                  region: pickRegion,
                  points: confidence,
                  confidence,
                  pickedAt: (updatedPick as any).pickedAt || new Date().toISOString(),
                  powerUpsUsed: [],
                  isCorrect: undefined,
                };

                state.picks[gameId] = pick;
                state.completedPicks = Object.keys(state.picks).length;
                state.isUpdatingPick = false;
              });

              propagateWinnerAndClearInvalid(gameId, teamIdTrimmed);
              tracePickFlow('write.success', {
                attemptId,
                bracketId: activeBracketId,
                gameId,
                teamId: teamIdTrimmed,
              });
              return;
            } catch (error: any) {
              if (updatePickSeqByGame[gameId] !== mySeq) {
                tracePickFlow('write.stale.error', {
                  attemptId,
                  gameId,
                  mySeq,
                  latest: updatePickSeqByGame[gameId],
                });
                return;
              }

              const status = getErrorStatus(error);
              const message = String(error?.message || '').toLowerCase();

              if (
                !recoveredOnce &&
                allowRetry &&
                (status === 404 || status === 410 || status === 403 || status === 401 ||
                 message.includes('no bracket loaded') ||
                 message.includes('bracket is not loaded') ||
                 message.includes('not found'))
              ) {
                recoveredOnce = true;
                tracePickFlow('write.recover.attempt', {
                  attemptId,
                  gameId,
                  status,
                  message,
                });
                try {
                  await tryRecoverBracket();
                  if (get().bracket) {
                    bracket = get().bracket;
                    set((state) => {
                      state.error = null;
                    });
                    if (bracket?.isLocked) {
                      set((state) => {
                        state.error = 'Bracket is locked and cannot be modified';
                        state.isUpdatingPick = false;
                      });
                      return;
                    }
                    await operation(false);
                    return;
                  }

                  await get().loadBracketFallbackToOwned();
                  bracket = get().bracket;
                  if (bracket) {
                    set((state) => {
                      state.error = null;
                    });
                    await operation(false);
                    return;
                  }
                } catch {
                  // fall through to user-visible error
                }
              }

              tracePickFlow('write.failure', {
                attemptId,
                gameId,
                status,
                message,
                routeScope,
              });
              const finalErrorMessage = message.includes('no bracket loaded')
                ? 'Could not save your pick yet. Open your active bracket and try again.'
                : (error?.message || 'Failed to update pick');

              if (!get().bracket && allowRetry) {
                tracePickFlow('write.failure.reacquire', {
                  attemptId,
                  gameId,
                  routeScope,
                });
                try {
                  await tryRecoverBracket();
                  if (get().bracket) {
                    set((state) => {
                      state.error = null;
                    });
                    await operation(false);
                    return;
                  }
                } catch {
                  // ignore and fall through
                }
              }

              const finalBracket = get().bracket;
              if (!finalBracket && allowRetry) {
                tracePickFlow('write.failure.finalFallback', {
                  attemptId,
                  gameId,
                  routeScope,
                });
                try {
                  await tryRecoverBracket();
                  const recovered = get().bracket;
                  if (!recovered && routeScope) {
                    await get().loadUserBracket();
                  }
                  const postRecover = get().bracket;
                  if (postRecover) {
                    set((state) => {
                      state.error = null;
                      state.isUpdatingPick = false;
                    });
                    if (postRecover.isLocked) {
                      set((state) => {
                        state.error = 'Bracket is locked and cannot be modified';
                        state.isUpdatingPick = false;
                      });
                    } else {
                      await operation(false);
                    }
                    return;
                  }
                } catch {
                  // fall through
                }
              }

              set((state) => {
                state.error = finalErrorMessage;
                state.isUpdatingPick = false;
                if (!get().bracket) {
                  state.error = 'Could not update this pick right now. Open your active bracket to continue.';
                }
              });
            }
          };

          return runPickWrite(queueScope, operation);
        },


        submitBracket: async (tiebreakerPoints?: number) => {
          const { bracket, picks } = get();
          
          if (!bracket) {
            set((state) => {
              state.error = 'No bracket loaded';
            });
            return;
          }

          if (Object.keys(picks).length < TOTAL_GAMES) {
            set((state) => {
              state.error = `Please make all ${TOTAL_GAMES} picks before submitting`;
            });
            return;
          }

          try {
            set((state) => {
              state.isSubmitting = true;
              state.error = null;
            });

            await bracketApi.submitBracket(bracket.id, tiebreakerPoints !== undefined ? { finalComments: String(tiebreakerPoints) } : undefined);

            set((state) => {
              state.isSubmitted = true;
              state.isSubmitting = false;
              if (state.bracket) {
                state.bracket.isLocked = true;
              }
            });

          } catch (error: any) {
            set((state) => {
              state.error = error.message || 'Failed to submit bracket';
              state.isSubmitting = false;
            });
          }
        },

        loadBracket: async (bracketId: string) => {
          try {
            traceBracketState('loadBracket.start', bracketId, { previousId: get().bracket?.id || null, isBootstrapping: get().isBootstrapping, isLoadingBracket: get().isLoadingBracket });
            set((state) => {
              state.isLoadingBracket = true;
              state.error = null;
            });

            const bracket = await bracketApi.getBracket(bracketId);
            const normalizedBracket = normalizeBracket(bracket);

            set((state) => {
              state.lastRequestedBracketId = bracketId;
              const nextId = normalizedBracket?.id || null;
              traceBracketState('loadBracket.success', nextId, {
                requested: bracketId,
                prevId: state.bracket?.id || null,
                routeScope: bracketId,
                teamCount: Array.isArray(normalizedBracket?.picks) ? normalizedBracket.picks.length : 0,
              });
              loadPicksFromBracket(state, normalizedBracket);
              state.isLoadingBracket = false;
            });

          } catch (error: any) {
            const status = getErrorStatus(error);
            const previousState = {
              bracket: get().bracket,
              lastRequestedBracketId: get().lastRequestedBracketId,
              picks: get().picks,
              completedPicks: get().completedPicks,
              error: get().error,
            };

            if (status === 404 || status === 401 || status === 403 || status === 410) {
              if (!previousState.bracket) {
                set((state) => {
                  state.error = 'This bracket is unavailable or no longer belongs to this account. Trying your active bracket...';
                });
              } else if (!previousState.error) {
                set((state) => {
                  state.error = 'This bracket could not be refreshed. Keeping your active bracket open.';
                });
              }

              await get().loadBracketFallbackToOwned(bracketId);

              set((state) => {
                state.lastRequestedBracketId = state.bracket?.id || previousState.lastRequestedBracketId;
                if (!state.bracket && previousState.bracket) {
                  // preserve prior bracket state on failure so the page does not hard-drop
                  state.bracket = previousState.bracket;
                  state.picks = previousState.picks;
                  state.completedPicks = previousState.completedPicks;
                  traceBracketState('loadBracket.fallback-preserve', previousState.bracket?.id || null, { requested: bracketId, status });
                }
                if (!state.bracket) {
                  state.error = state.error || 'This bracket is unavailable or no longer belongs to this account.';
                } else {
                  state.error = null;
                }
                state.isLoadingBracket = false;
              });
              return;
            }

            traceBracketState('loadBracket.error', previousState.bracket ? previousState.bracket.id : null, {
              requested: bracketId,
              reason: error?.message || 'Failed to load bracket',
              status,
            });
            set((state) => {
              clearErrorToNoBracket(state, error?.message || 'Failed to load bracket', bracketId);
            });
          }
        },

        loadBracketFallbackToOwned: async (targetBracketId?: string) => {
          try {
            const brackets = await bracketApi.getUserBrackets();
            const arr = Array.isArray(brackets) ? brackets : [];
            const target = targetBracketId
              ? arr.find((b: any) => [b.id, b.bracketId, b.bracket_id, b._id].includes(targetBracketId))
              : null;
            const activeBracket =
              target ||
              arr.find((b: any) => b.isActive || b.is_active) ||
              (arr.length > 0 ? arr[0] : null);

            if (!activeBracket) {
              set((state) => {
                if (!state.lastStableBracket) {
                  state.bracket = null;
                  state.lastRequestedBracketId = null;
                  state.picks = {};
                  state.completedPicks = 0;
                  traceBracketState('loadBracketFallback.empty', null, { routeScope: targetBracketId || 'active' });
                }
                state.error = 'No accessible brackets found.';
              });
              return null;
            }

            const normalized = normalizeBracket(activeBracket);

            set((state) => {
              state.lastRequestedBracketId = normalized.id || null;
              const nextId = normalized.id || null;
              traceBracketState('loadBracketFallback.success', nextId, { previousId: state.bracket?.id || null, target: targetBracketId || null });
              loadPicksFromBracket(state, normalized);
              state.error = null;
            });
            return normalized as Bracket;
          } catch {
            set((state) => {
              state.error = 'Failed to load your active bracket.';
            });
            return null;
          } finally {
            set((state) => {
              state.isLoadingBracket = false;
            });
          }
        },

        loadUserBracket: async () => {
          const myLoadId = ++loadUserBracketSeq;

          set((state) => {
            state.isBootstrapping = true;
            state.error = null;
          });

          try {
            // Add a hard timeout so we never hang on "Loading bracket..."
            const brackets = await Promise.race([
              bracketApi.getUserBrackets(),
              new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('Load bracket timed out')), 12000)
              ),
            ]);

            // Ignore stale responses
            if (myLoadId !== loadUserBracketSeq) return;

            if (brackets && brackets.length > 0) {
              const activeBracket = brackets.find((b: Bracket) => b.isActive) || brackets[0];

              set((state) => {
                const normalized = normalizeBracket(activeBracket);
                state.lastRequestedBracketId = normalized.id || null;
                const nextId = normalized.id || null;
                traceBracketState('loadUserBracket.success', nextId, { previousId: state.bracket?.id || null });
                state.bracket = normalized as Bracket;
                state.lastStableBracket = normalized || null;
                state.isSubmitted = normalized.isLocked;

                state.picks = {};
                if (normalized.picks) {
                  normalized.picks.forEach((pick: BracketPick) => {
                    const game = getGameById(pick.gameId);
                    state.picks[pick.gameId] = {
                      ...pick,
                      region: pick.region || game?.region || 'east',
                    };
                  });
                }

                state.completedPicks = Object.keys(state.picks).length;
              });
            } else {
              set((state) => {
                if (!state.lastStableBracket) {
                  state.bracket = null;
                  state.lastRequestedBracketId = null;
                  state.picks = {};
                  state.completedPicks = 0;
                  traceBracketState('loadBracketFallback.empty', null, { routeScope: 'userFallback' });
                }
                state.error = null;
              });
            }
          } catch (error: any) {
            if (myLoadId !== loadUserBracketSeq) return;

            set((state) => {
              if (error?.status === 404 || error?.status === 401) {
                if (!state.lastStableBracket) {
                  state.bracket = null;
                  state.picks = {};
                  state.completedPicks = 0;
                  traceBracketState('loadUserBracket.error', null, { status: error?.status, routeScope: 'user' });
                }
                state.error = 'Could not load your active bracket. Please sign in again.';
              } else {
                state.error = error?.message || 'Failed to load bracket';
              }
            });
          } finally {
            // Only the latest call may clear isBootstrapping
            if (myLoadId === loadUserBracketSeq) {
              set((state) => {
                state.isBootstrapping = false;
              });
            }
          }
        },

        createBracket: async (name: string, tournamentId: string, description?: string) => {
          try {
            set((state) => {
              state.isCreatingBracket = true;
              state.error = null;
            });

            const newBracket = await bracketApi.createBracket({
              name,
              tournamentId,
              description,
            });

            set((state) => {
              const nextId = newBracket?.id || null;
              traceBracketState('createBracket.success', nextId, { previousId: state.bracket?.id || null });
              state.bracket = newBracket;
              state.lastStableBracket = newBracket || null;
              state.isSubmitted = false;
              state.picks = {};
              state.completedPicks = 0;
              state.isCreatingBracket = false;
            });

          } catch (error: any) {
            set((state) => {
              state.error = error.message || 'Failed to create bracket';
              state.isCreatingBracket = false;
            });
            throw error;
          }
        },

        resetBracket: () => {
          set((state) => {
            traceBracketState('resetBracket', null, { previousId: state.bracket?.id || null });
            state.bracket = null;
            state.lastRequestedBracketId = null;
            state.picks = {};
            state.isBootstrapping = false;
            state.isLoadingBracket = false;
            state.isCreatingBracket = false;
            state.isUpdatingPick = false;
            state.isSubmitting = false;
            state.isSubmitted = false;
            state.error = null;
            state.completedPicks = 0;
          });
        },

        clearError: () => {
          set((state) => {
            state.error = null;
          });
        },

        // Computed getters
        getPicksProgress: () => {
          const { completedPicks, totalPicks } = get();
          return {
            completed: completedPicks,
            total: totalPicks,
            percentage: Math.round((completedPicks / totalPicks) * 100),
          };
        },

        getRegionProgress: (region: BracketRegion) => {
          const total = 15; // 15 games per region (Rounds 1-4)
          const completed = Object.values(get().picks).filter((pick: BracketPick) => {
            const game = getGameById(pick.gameId);
            return game?.region === region;
          }).length;
          return {
            completed,
            total,
            percentage: Math.min(100, Math.round((completed / total) * 100)),
          };
        },

        getPickForGame: (gameId: string) => {
          return get().picks[gameId] || null;
        },

        getRegionPicks: (region: BracketRegion) => {
          const { picks } = get();
          return Object.values(picks).filter((pick: BracketPick) => {
            const game = getGameById(pick.gameId);
            return game?.region === region;
          });
        },

        canSubmitBracket: () => {
          const { bracket, picks, isSubmitting } = get();
          return !!(
            bracket && 
            !bracket.isLocked && 
            Object.keys(picks).length === TOTAL_GAMES && 
            !isSubmitting
          );
        },
      };
    })
    ),
    {
      name: 'bracket-store',
    }
  )
);

// Selector hooks for performance
export const useBracketPicks = () => useBracketStore(state => state.picks);
export const useBracketProgress = () => useBracketStore(state => 
  state.getPicksProgress()
);
export const useBracketLoading = () => useBracketStore(state => state.isBootstrapping);
export const useBracketPickUpdating = () => useBracketStore(state => state.isUpdatingPick);
export const useBracketCreating = () => useBracketStore(state => state.isCreatingBracket);
export const useBracketFetching = () => useBracketStore(state => state.isLoadingBracket);
export const useBracketError = () => useBracketStore(state => state.error);